import { useMaybeRoomContext } from "@livekit/components-react";
import { useCallback, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { AudioMethodName, UnityObjectName } from "../../utils/const";
import { ConnectionState, RoomEvent } from "livekit-client";

function Rooms({ sendMessage }) {
    const room = useMaybeRoomContext();
    const audioContextRef = useRef(null);
    const unityRNSyncingDelay = 0.2; // in seconds

    // Memoized function to send messages to Unity, only sends when content changes
    const sendMessageToUnity = useCallback(
        (audio, channels) => {
            const msg = JSON.stringify({ audio, channels });
            sendMessage(UnityObjectName, AudioMethodName, msg);
        },
        [sendMessage]
    );

    useEffect(() => {
        if (!room) return;

        console.log("Room context", room);

        const handleDataReceived = (data) => {
            console.log("Room context DataReceived", data);
        };

        const handleTrackSubscribed = async (track) => {
            if (track.kind !== "audio") return;

            if (!audioContextRef.current) {
                audioContextRef.current = new AudioContext();
            }

            const audioContext = audioContextRef.current;
            const bufferSize = 4096;
            const delayInSamples = Math.floor(
                audioContext.sampleRate * unityRNSyncingDelay
            );
            console.log(delayInSamples);
            const mediaStream = new MediaStream();
            mediaStream.addTrack(track.mediaStreamTrack);
            const sourceNode =
                audioContext.createMediaStreamSource(mediaStream);

            // Register AudioWorklet modules
            await audioContext.audioWorklet.addModule(
                "/recording-processor.js"
            );
            await audioContext.audioWorklet.addModule("/playback-processor.js");

            const recordingProcessor = new AudioWorkletNode(
                audioContext,
                "recording-processor"
            );
            const playbackProcessor = new AudioWorkletNode(
                audioContext,
                "playback-processor",
                {
                    processorOptions: { bufferSize, delayInSamples },
                }
            );

            // Connect recording processor to playback processor via port communication
            recordingProcessor.port.onmessage = (event) => {
                const { inputBuffer } = event.data;
                playbackProcessor.port.postMessage({ inputBuffer });
            };

            sourceNode.connect(recordingProcessor);
            recordingProcessor.connect(audioContext.destination); // Keep recording processor alive
            playbackProcessor.connect(audioContext.destination);

            console.log("Audio pipeline set up with delay.");

            // Setup an analyser node
            const analyser = audioContext.createAnalyser();
            sourceNode.connect(analyser);
            analyser.fftSize = 2048;

            const timeDomainArray = new Uint8Array(analyser.fftSize);

            const getAudioData = () => {
                analyser.getByteTimeDomainData(timeDomainArray);
                // Send the data to Unity
                const base64String = btoa(
                    String.fromCharCode(...timeDomainArray)
                );
                sendMessageToUnity(base64String, 1);

                if (room.state === ConnectionState.Connected) {
                    requestAnimationFrame(getAudioData);
                }
            };

            getAudioData();
        };

        room.on(RoomEvent.DataReceived, handleDataReceived);
        room.on(RoomEvent.TrackSubscribed, handleTrackSubscribed);

        return () => {
            room.off(RoomEvent.DataReceived, handleDataReceived);
            room.off(RoomEvent.TrackSubscribed, handleTrackSubscribed);
        };
    }, [room, sendMessageToUnity]);

    return null;
}

Rooms.propTypes = {
    sendMessage: PropTypes.func.isRequired,
};

export default Rooms;
